import React, { useCallback, useRef } from 'react';
import { ReactFlow, Controls, MiniMap, Background } from '@xyflow/react';
import '@xyflow/react/dist/style.css';

import TriggerNode from './nodes/TriggerNode';
import ConditionNode from './nodes/ConditionNode';
import ActionNode from './nodes/ActionNode';
import FlowEdge from './edges/FlowEdge';
import { NODE_TYPES, NODE_STYLES } from '../../constants';

const nodeTypes = {
  [NODE_TYPES.TRIGGER]: TriggerNode,
  [NODE_TYPES.CONDITION]: ConditionNode,
  [NODE_TYPES.ACTION]: ActionNode,
};

const edgeTypes = {
  flow: FlowEdge,
};

const defaultEdgeOptions = {
  type: 'flow',
  animated: true,
};

const defaultViewport = { x: 0, y: 0, zoom: 1 };

const minimapNodeColor = (node) => NODE_STYLES[node.type]?.hex || '#94a3b8';

const FlowCanvas = ({
  nodes,
  edges,
  onNodesChange,
  onEdgesChange,
  onConnect,
  onNodeClick,
  onPaneClick,
  addNode,
}) => {
  const reactFlowInstance = useRef(null);

  const onDragOver = useCallback((event) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
  }, []);

  const onDrop = useCallback((event) => {
    event.preventDefault();

    const nodeType = event.dataTransfer.getData('application/reactflow/type');
    const nodeSubType = event.dataTransfer.getData('application/reactflow/subtype');

    if (!nodeType || !reactFlowInstance.current) return;

    const position = reactFlowInstance.current.screenToFlowPosition({
      x: event.clientX,
      y: event.clientY,
    });

    addNode(nodeType, position, nodeSubType);
  }, [addNode]);

  const onInit = useCallback((instance) => {
    reactFlowInstance.current = instance;
  }, []);

  return (
    <div className="h-full w-full">
      <ReactFlow
        nodes={nodes}
        edges={edges}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        onConnect={onConnect}
        onNodeClick={onNodeClick}
        onPaneClick={onPaneClick}
        onInit={onInit}
        onDrop={onDrop}
        onDragOver={onDragOver}
        nodeTypes={nodeTypes}
        edgeTypes={edgeTypes}
        defaultEdgeOptions={defaultEdgeOptions}
        defaultViewport={defaultViewport}
        deleteKeyCode={['Backspace', 'Delete']}
        className="bg-gray-50"
      >
        <Controls position="bottom-left" />
        <MiniMap
          position="bottom-right"
          nodeColor={minimapNodeColor}
          nodeStrokeWidth={3}
          maskColor="rgba(0, 0, 0, 0.08)"
          zoomable
          pannable
        />
        <Background/>
      </ReactFlow>
    </div>
  );
};

export default FlowCanvas;
